import { useMemo, useState } from 'react';
import {
  InfiniteData,
  UseMutationResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  APIError,
  DefaultQueryParams,
  FunnelActivityQueryParams,
  TrialVolunteerAnswersParams,
} from '@data';
import {
  funnelActivityKeys,
  funnelActivityUsersKeys,
  leadSponsorKeys,
  matchedVolunteersKeys,
  publicTrialLocationsKeys,
  publicTrialsKeys,
  teamMandatesKeys,
  trialActivityKeys,
  trialContactsKeys,
  trialCriteriaKeys,
  trialLocationsKeys,
  trialSummaryTranslationKeys,
  trialTitleTranslationKeys,
  trialsKeys,
  volunteerAnswersKeys,
  volunteerContactsKeys,
  volunteerOverviewKeys,
} from '@data/constants';
import {
  CriteriaRequestBody,
  ListLocationView,
  ListTrialCriteriaView,
  ListTrialMatchView,
  ListTrialTranslationView,
  ListTrialView,
  ListTrialVolunteerActivityUsersView,
  ListTrialVolunteerActivityView,
  TrialBoosterBody,
  TrialContactBody,
  TrialCriteriaBody,
  TrialCriteriaView,
  TrialDetailsView,
  TrialLocationBody,
  TrialTranslationBody,
  TrialVolunteerAnswersListView,
  TrialVolunteerContactView,
  UpdateTrialBody,
  TrialVolunteerFeedbackBody,
  TrialManagementApiSearchRequest,
  TrialVolunteersOverviewView,
  TrialManagementApiGetLeadSponsorsRequest,
  ListLeadSponsorView,
  TrialManagementApiGetLocationsRequest,
  TrialManagementApiGetPublicTrialsRequest,
  ListTrialPublicView,
  TrialCreateBody,
  CriteriaStatus,
  TrialMatchView,
  TrialReachOutView,
  LocationView,
  TrialView,
  ListPublicLocationView,
  TrialReachOutBodyCommunicationKeyEnum,
  TeamMandatesListView,
  TrialManagementApiGetTeamMandatesRequest,
  TrialManagementApiAssignMandateRequest,
  TrialManagementApiUnassignMandateRequest,
  TrialManagementApiGetMatchesRequest,
  TrialManagementApiAddEffectRequest,
  ListTrialLocationView,
  TrialManagementApiRemoveEffectRequest,
  ListTrialContactView,
} from '@data/sdk/match';
import { useAppUserStore } from '@data/storage';
import { MatchingApi } from '@data/utils/axios-instance';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useTrialsList = (params: TrialManagementApiSearchRequest['getTrialsBody']) => {
  const { user } = useAppUserStore.getState();
  return useQuery<ListTrialView, APIError>({
    queryKey: trialsKeys.list(user?.teamId, params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.search({ getTrialsBody: params });
      return response.data;
    },
  });
};

export const usePublicTrialsList = (params: TrialManagementApiGetPublicTrialsRequest) =>
  useQuery<ListTrialPublicView, Error>({
    queryKey: publicTrialsKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getPublicTrials(params);
      return response.data;
    },
  });

// TODO: move to queries
export const trialDetailQuery = (trialId: string) => ({
  queryKey: trialsKeys.detail(trialId),
  queryFn: async () => {
    const response = await MatchingApi.trialManagement.getDetails({ id: trialId });

    return response.data;
  },
});

export const useTrial = (trialId: string, initialData?: TrialDetailsView, enabled = true) => {
  return useQuery<TrialDetailsView, Error>({
    queryKey: trialsKeys.detail(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getDetails({ id: trialId });
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    initialData,
    enabled: !!trialId && enabled,
  });
};

export const usePublicTrialLocationsList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListPublicLocationView, Error>({
    queryKey: publicTrialLocationsKeys.list(trialId, { ...params }),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getPublicLocations({
        id: trialId,
        ...params,
      });
      return response.data;
    },
  });

export const usePublicTrialLocationsInfiniteList = (trialId: string) => {
  const [query, setQuery] = useState<DefaultQueryParams>({
    limit: 15,
    offset: 0,
    searchString: undefined,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isInitialLoading, isFetching, refetch } =
    useInfiniteQuery<ListPublicLocationView, APIError>({
      queryKey: trialId
        ? publicTrialLocationsKeys.list(trialId, { ...query, searchString: debouncedSearch })
        : undefined,

      queryFn: async ({
        pageParam = {
          ...query,
          searchString: debouncedSearch,
          id: trialId,
        },
      }) => {
        const response = await MatchingApi.trialManagement.getPublicLocations(pageParam);
        return response.data;
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

        return nextOffset < lastPage.count
          ? {
              limit: query.limit,
              offset: nextOffset,
              searchString: debouncedSearch || undefined,
              id: trialId,
            }
          : undefined;
      },

      keepPreviousData: true,
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60,
      enabled: !!trialId,
    });

  const locations = data?.pages.flatMap((page) => page.items) || [];

  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        ...location,
        label: `${location.city} (${location.country}), ${location.facility}`,
        value: location.id,
      })),
    [locations],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    locations,
    locationOptions,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    handleSearchChange,
    isInitialLoading,
    searchString: query.searchString,
    refetch,
  };
};

export const useTrialCriteriaList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialCriteriaView, Error>(
    trialCriteriaKeys.list(trialId, params),
    async () => {
      const response = await MatchingApi.trialManagement.getCriteria({ id: trialId, ...params });
      return response.data;
    },
    { enabled: !!trialId },
  );

export const useActivateAllTrialCriteria = (): UseMutationResult<
  unknown,
  APIError,
  { params: DefaultQueryParams; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.validateAll({ id: trialId });
      return response.data;
    },
    {
      onMutate: async ({ trialId, params }) => {
        await queryClient.cancelQueries(trialCriteriaKeys.all());

        const previousCriteriaList = queryClient.getQueryData<InfiniteData<ListTrialCriteriaView>>(
          trialCriteriaKeys.list(trialId, params),
        );

        if (previousCriteriaList) {
          const newPages = previousCriteriaList.pages.map(
            (page: { items: TrialCriteriaView[] }) => ({
              ...page,
              items: page.items.map((criteria) => ({
                ...criteria,
                status: CriteriaStatus.Validated,
              })),
            }),
          );

          queryClient.setQueryData(trialCriteriaKeys.list(trialId, params), {
            ...previousCriteriaList,
            pages: newPages,
          });
        }

        return { previousCriteriaList };
      },
      onSuccess: (_, { trialId }) => queryClient.invalidateQueries(trialsKeys.detail(trialId)),

      onError: (_, { trialId, params }, context) =>
        queryClient.setQueryData(
          trialCriteriaKeys.list(trialId, params),
          context?.previousCriteriaList,
        ),
    },
  );
};

export const useAddTrialComment = (): UseMutationResult<
  unknown,
  APIError<{ comment: string }>,
  { comment: string; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, comment }) => {
      const response = await MatchingApi.trialManagement.createComment({
        id: trialId,
        trialCommentBody: { comment },
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(trialActivityKeys.list(trialId));
      },
    },
  );
};

export const useRequestCriteria = (): UseMutationResult<
  unknown,
  APIError<CriteriaRequestBody>,
  { trialId: string; values: CriteriaRequestBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.requestCriteria({
        id: trialId,
        criteriaRequestBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(trialActivityKeys.list(trialId));
      },
    },
  );
};

export const useUpdateTrial = (): UseMutationResult<
  unknown,
  APIError,
  { trialId: string; values: UpdateTrialBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.updateTrial({
        id: trialId,
        updateTrialBody: values,
      });
      return response.data;
    },
    {
      onMutate: async ({ trialId, values }) => {
        await queryClient.cancelQueries(trialsKeys.all());

        const previousTrial = queryClient.getQueryData<TrialDetailsView>(
          trialsKeys.detail(trialId),
        );

        if (previousTrial) {
          const newTrial = { ...previousTrial, managedBy: values.managedBy };

          queryClient.setQueryData(trialsKeys.detail(trialId), newTrial);
        }

        return { previousTrial };
      },
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(trialsKeys.all());
        queryClient.invalidateQueries(trialActivityKeys.list(trialId));
        queryClient.invalidateQueries(trialCriteriaKeys.all());
      },

      onError: (_, { trialId }, context) =>
        queryClient.setQueryData(trialsKeys.detail(trialId), context?.previousTrial),
    },
  );
};

export const useTrialContactsList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialContactView, Error>(
    trialContactsKeys.list(trialId),
    async () => {
      const response = await MatchingApi.trialManagement.getContacts({ id: trialId, ...params });
      return response.data;
    },
    { enabled: !!trialId, keepPreviousData: true },
  );

export const useAddTrialContact = (): UseMutationResult<
  unknown,
  APIError<TrialContactBody>,
  { trialId: string; values: TrialContactBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.createContact({
        id: trialId,
        trialContactBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(trialContactsKeys.list(trialId));
        queryClient.invalidateQueries(trialsKeys.detail(trialId));
      },
    },
  );
};

export const useTrialLocation = (locationId: string) =>
  useQuery<LocationView, Error>(
    trialLocationsKeys.detail(locationId),
    async () => {
      const response = await MatchingApi.location.getLocation({ id: locationId });
      return response.data;
    },
    { enabled: !!locationId },
  );

export const useTrialLocationsList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialLocationView, APIError>(
    trialLocationsKeys.list({ id: trialId, ...params }),
    async () => {
      const response = await MatchingApi.trialManagement.getLocations({ id: trialId, ...params });
      return response.data;
    },
    { enabled: !!trialId },
  );

export const useTrialLocationsListInfinite = (trialId: string) => {
  const [query, setQuery] = useState<TrialManagementApiGetLocationsRequest>({
    limit: 20,
    offset: 0,
    searchString: undefined,
    id: trialId,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchedAfterMount,
  } = useInfiniteQuery<ListLocationView, APIError>({
    queryKey: trialLocationsKeys.list({ id: trialId, searchString: debouncedSearch }),

    queryFn: async ({
      pageParam = <TrialManagementApiGetLocationsRequest>{
        ...query,
        searchString: debouncedSearch,
        id: trialId,
      },
    }) => {
      const response = await MatchingApi.trialManagement.getLocations(pageParam);
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            id: trialId,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
    enabled: !!trialId,
  });

  const trialLocations = data?.pages.flatMap((page) => page.items) || [];

  const trialLocationOptions = useMemo(
    () =>
      trialLocations.map((location) => ({
        label: `${location.city} (${location.country}) ${
          location.facility ? `, ${location.facility}` : ''
        }`,
        value: location.id,
        permission: `contacts:${location.id}:read`,
      })),
    [trialLocations],
  );

  const trialLocationsCount = data?.pages[0].count || 0;

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    handleSearchChange,
    trialLocationOptions,
    trialLocationsCount,
    trialLocations,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchedAfterMount,
  };
};

export const useAddTrialLocation = (): UseMutationResult<
  LocationView,
  APIError<TrialLocationBody>,
  { trialId: string; values: TrialLocationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.createLocation({
        id: trialId,
        trialLocationBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(trialsKeys.detail(trialId));
        queryClient.invalidateQueries(trialLocationsKeys.all());
      },
    },
  );
};

export const useAddCriteria = (): UseMutationResult<
  unknown,
  APIError<TrialCriteriaBody>,
  { trialId: string; values: TrialCriteriaBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.createCriteria({
        id: trialId,
        trialCriteriaBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(trialCriteriaKeys.all());
        queryClient.invalidateQueries(trialsKeys.detail(trialId));
      },
    },
  );
};

export const useMarkTrialAsFavorite = (): UseMutationResult<
  unknown,
  Error,
  { params?: TrialManagementApiSearchRequest['getTrialsBody']; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  const { user } = useAppUserStore.getState();

  return useMutation(
    async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.addFavoriteTrial({ id: trialId });
      return response.data;
    },
    {
      onMutate: async ({ trialId, params }) => {
        await queryClient.cancelQueries(trialsKeys.all());

        const previousTrial = queryClient.getQueryData<TrialDetailsView>(
          trialsKeys.detail(trialId),
        );

        if (previousTrial) {
          const newTrial = { ...previousTrial, isFavorite: true };

          queryClient.setQueryData(trialsKeys.detail(trialId), newTrial);
        }

        const previousTrialsList = queryClient.getQueryData<ListTrialView>(
          trialsKeys.list(user?.teamId, params),
        );

        if (previousTrialsList) {
          const newTrialsList = previousTrialsList.items.map((trial) =>
            trial.id === trialId ? { ...trial, isFavorite: true } : trial,
          );

          queryClient.setQueryData(trialsKeys.list(user?.teamId, params), {
            ...previousTrialsList,
            items: newTrialsList,
          });

          queryClient.getQueryData<ListTrialView>(trialsKeys.list(user?.teamId, params));
        }

        return { previousTrial, previousTrialsList };
      },

      onError: (_, { trialId, params }, context) => {
        queryClient.setQueryData(trialsKeys.detail(trialId), context?.previousTrial);
        queryClient.setQueryData(
          trialsKeys.list(user?.teamId, params),
          context?.previousTrialsList,
        );
      },
    },
  );
};

export const useUnMarkTrialAsFavorite = (): UseMutationResult<
  unknown,
  any,
  { params?: TrialManagementApiSearchRequest['getTrialsBody']; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  const { user } = useAppUserStore.getState();
  return useMutation(
    async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.deleteFavoriteTrial({ id: trialId });
      return response.data;
    },
    {
      onMutate: async ({ trialId, params }) => {
        await queryClient.cancelQueries(trialsKeys.detail(trialId));

        const previousTrial = queryClient.getQueryData<TrialDetailsView>(
          trialsKeys.detail(trialId),
        );

        if (previousTrial) {
          const newTrial = { ...previousTrial, isFavorite: false };

          queryClient.setQueryData(trialsKeys.detail(trialId), newTrial);
        }

        const previousTrialsList = queryClient.getQueryData<ListTrialView>(
          trialsKeys.list(user?.teamId, params),
        );

        if (previousTrialsList) {
          const newTrialsList = previousTrialsList.items.map((trial) =>
            trial.id === trialId ? { ...trial, isFavorite: false } : trial,
          );

          queryClient.setQueryData(trialsKeys.list(user?.teamId, params), {
            ...previousTrialsList,
            items: newTrialsList,
          });

          queryClient.getQueryData<ListTrialView>(trialsKeys.list(user?.teamId, params));
        }

        return { previousTrial, previousTrialsList };
      },

      onError: (_, { trialId, params }, context) => {
        queryClient.setQueryData(trialsKeys.detail(trialId), context?.previousTrial);
        queryClient.setQueryData(
          trialsKeys.list(user?.teamId, params),
          context?.previousTrialsList,
        );
      },
    },
  );
};

export const useDeleteTrial = (): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation(
    async (trialId) => {
      const response = await MatchingApi.trialManagement.deleteTrial({ id: trialId });
      return response.data;
    },
    {
      onSuccess: (_, trialId) => {
        queryClient.invalidateQueries(trialsKeys.detail(trialId));
      },
    },
  );
};

export const useRestoreTrial = (): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation(
    async (trialId) => {
      const response = await MatchingApi.trialManagement.restoreTrial({ id: trialId });
      return response.data;
    },
    {
      onSuccess: (_, trialId) => {
        queryClient.invalidateQueries(trialsKeys.detail(trialId));
      },
    },
  );
};

export const useTrialTitleTranslations = (trialId: string) =>
  useQuery<ListTrialTranslationView, Error>(
    trialTitleTranslationKeys.list(trialId),
    async () => {
      const response = await MatchingApi.trialManagement.getTitleTranslations({ id: trialId });
      return response.data;
    },
    { enabled: !!trialId },
  );

export const useCreateTrialTitleTranslations = (): UseMutationResult<
  ListTrialTranslationView,
  APIError,
  { id: string; values: TrialTranslationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, values }) => {
      const response = await MatchingApi.trialManagement.createTitleTranslations({
        id,
        trialTranslationBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(trialTitleTranslationKeys.list(id));
      },
    },
  );
};

export const useTrialSummaryTranslations = (trialId: string) =>
  useQuery<ListTrialTranslationView, Error>(
    trialSummaryTranslationKeys.list(trialId),
    async () => {
      const response = await MatchingApi.trialManagement.getSummaryTranslations({ id: trialId });
      return response.data;
    },
    { enabled: !!trialId },
  );

export const useCreateTrialSummaryTranslations = (): UseMutationResult<
  ListTrialTranslationView,
  APIError,
  { id: string; values: TrialTranslationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, values }) => {
      const response = await MatchingApi.trialManagement.createSummaryTranslations({
        id,
        trialTranslationBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(trialSummaryTranslationKeys.list(id));
      },
    },
  );
};

// funnel page
export const useVolunteersOverview = (trialId: string, locationId?: string) =>
  useQuery<TrialVolunteersOverviewView, Error>(
    volunteerOverviewKeys.listByCountry(trialId, locationId),
    async () => {
      const response = await MatchingApi.trialManagement.getVolunteersOverview({
        id: trialId,
        ...(locationId && { locationId }),
      });
      return response.data;
    },
    { enabled: !!trialId, refetchOnReconnect: true },
  );

export const useVolunteerAnswersMutate = (): UseMutationResult<
  TrialVolunteerAnswersListView,
  APIError,
  { params: TrialVolunteerAnswersParams; trialId: string },
  unknown
> => {
  return useMutation(async ({ trialId, params }) => {
    const response = await MatchingApi.trialManagement.getVolunteerAnswers({
      id: trialId,
      ...params,
    });
    return response.data;
  });
};

export const useMatchedVolunteers = (
  trialId: string,
  params: Omit<TrialManagementApiGetMatchesRequest, 'id'>,
  locationId?: string,
) => {
  return useQuery<ListTrialMatchView, Error>(
    matchedVolunteersKeys.list({ id: trialId, ...params, locationId }),
    async () => {
      const response = await MatchingApi.trialManagement.getMatches({
        id: trialId,
        ...params,
        locationId: locationId || undefined,
      });
      return response.data;
    },
    { enabled: !!trialId },
  );
};

export const useVolunteerContacts = (trialId: string, volunteerId: string) =>
  useQuery<TrialVolunteerContactView, Error>(
    volunteerContactsKeys.list(volunteerId),
    async () => {
      const response = await MatchingApi.trialManagement.getVolunteerContact({
        id: trialId,
        userId: volunteerId,
      });
      return response.data;
    },
    { enabled: !!trialId && !!volunteerId },
  );

export const useVolunteerContactsMutate = (): UseMutationResult<
  TrialVolunteerContactView,
  APIError,
  { trialId: string; volunteerId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, volunteerId }) => {
      const response = await MatchingApi.trialManagement.getVolunteerContact({
        id: trialId,
        userId: volunteerId,
      });
      return response.data;
    },
    {
      onSuccess: (data, { volunteerId }) => {
        queryClient.setQueryData(volunteerContactsKeys.list(volunteerId), data);
      },
    },
  );
};

export const useUpdateVolunteerStatusInTrial = (): UseMutationResult<
  unknown,
  APIError,
  { trialId: string; values: TrialVolunteerFeedbackBody; volunteerId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, volunteerId, values }) => {
      const response = await MatchingApi.trialManagement.respondToVolunteer({
        id: trialId,
        userId: volunteerId,
        trialVolunteerFeedbackBody: values,
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(volunteerAnswersKeys.all());
        queryClient.invalidateQueries(volunteerOverviewKeys.list(trialId));
      },
    },
  );
};

export const useFunnelActivity = (trialId: string, params: FunnelActivityQueryParams) =>
  useQuery<ListTrialVolunteerActivityView, Error>(
    funnelActivityKeys.list(params),
    async () => {
      const response = await MatchingApi.trialManagement.getVolunteerActivity({
        id: trialId,
        ...params,
      });
      return response.data;
    },
    { enabled: !!trialId },
  );

export const useFunnelActivityUsers = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialVolunteerActivityUsersView, Error>(
    funnelActivityUsersKeys.list(params),
    async () => {
      const response = await MatchingApi.trialManagement.getVolunteerActivityUsers({
        id: trialId,
        ...params,
      });
      return response.data;
    },
    { enabled: !!trialId },
  );

export const useRequestBooster = (): UseMutationResult<
  unknown,
  APIError,
  { trialId: string; values: TrialBoosterBody },
  unknown
> => {
  return useMutation(async ({ trialId, values }) => {
    const response = await MatchingApi.trialManagement.requestBooster({
      id: trialId,
      trialBoosterBody: values,
    });
    return response.data;
  });
};

export const useLeadSponsorsInfiniteList = () => {
  const [query, setQuery] = useState<TrialManagementApiGetLeadSponsorsRequest>({
    limit: 15,
    offset: 0,
    searchString: undefined,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListLeadSponsorView,
    APIError
  >({
    queryKey: leadSponsorKeys.list({ ...query, searchString: debouncedSearch }),

    queryFn: async ({ pageParam = { ...query, searchString: debouncedSearch } }) => {
      const response = await MatchingApi.trialManagement.getLeadSponsors(pageParam);
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const leadSponsors = data?.pages.flatMap((page) => page.items) || [];

  const leadSponsorsOptions = useMemo(
    () =>
      leadSponsors.map((leadSponsor) => ({
        label: leadSponsor.name,
        value: leadSponsor.name,
      })),
    [leadSponsors],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    leadSponsors,
    leadSponsorsOptions,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoading || isFetching,
    isFirstLoading: isLoading,
    handleSearchChange,
    searchString: query.searchString,
  };
};

export const useCreateTrial = (): UseMutationResult<
  TrialView,
  APIError,
  { values: TrialCreateBody },
  unknown
> => {
  return useMutation(async ({ values }) => {
    const response = await MatchingApi.trialManagement.createTrial({ trialCreateBody: values });
    return response.data;
  });
};

export const useUpdateVolunteerContacted = (): UseMutationResult<
  unknown,
  APIError,
  {
    contactedAt: string;
    locationId?: string;
    params?: Omit<TrialManagementApiGetMatchesRequest, 'id'>;
    trialId: string;
    userId: string;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ contactedAt, trialId, userId }) => {
      const response = await MatchingApi.trialManagement.updateVolunteerContacted({
        trialVolunteerContactBody: { contactedAt },
        id: trialId,
        userId,
      });
      return response.data;
    },
    {
      onMutate: async ({ trialId, contactedAt, userId, params, locationId }) => {
        await queryClient.cancelQueries(matchedVolunteersKeys.all());

        const previousVolunteersList = queryClient.getQueryData<ListTrialMatchView>(
          matchedVolunteersKeys.list({ id: trialId, showDetails: false, ...params, locationId }),
        );

        if (previousVolunteersList) {
          const newVolunteersList = previousVolunteersList.items.map((user: TrialMatchView) =>
            user.userId === userId ? { ...user, contactedAt } : user,
          );
          queryClient.setQueryData(
            matchedVolunteersKeys.list({ id: trialId, showDetails: false, ...params, locationId }),
            {
              ...previousVolunteersList,
              items: newVolunteersList,
            },
          );
        }
        return { previousVolunteersList };
      },

      onError: (_, { trialId, params }, context) =>
        queryClient.setQueryData(
          matchedVolunteersKeys.list({ id: trialId, showDetails: false, ...params }),
          context?.previousVolunteersList,
        ),
    },
  );
};

export const useVolunteerReachOut = (): UseMutationResult<
  TrialReachOutView,
  APIError,
  { communicationKey: TrialReachOutBodyCommunicationKeyEnum; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ trialId, communicationKey }) => {
      const response = await MatchingApi.trialManagement.volunteerReachOut({
        id: trialId,
        trialReachOutBody: { communicationKey },
      });
      return response.data;
    },
    {
      onSuccess: (_, { trialId }) => {
        queryClient.invalidateQueries(volunteerOverviewKeys.list(trialId));
        queryClient.invalidateQueries(trialsKeys.detail(trialId));
        queryClient.invalidateQueries(funnelActivityKeys.all());
      },
    },
  );
};

// Mandates
export const useTeamMandates = (filters: TrialManagementApiGetTeamMandatesRequest) =>
  useQuery<TeamMandatesListView, APIError>(
    teamMandatesKeys.list(filters),
    async () => {
      const response = await MatchingApi.trialManagement.getTeamMandates(filters);
      return response.data;
    },
    { enabled: !!filters.teamId },
  );

export const useAssignMandate = (): UseMutationResult<
  void,
  APIError,
  {
    body: TrialManagementApiAssignMandateRequest;
    filters: TrialManagementApiGetTeamMandatesRequest;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ body }) => {
      const response = await MatchingApi.trialManagement.assignMandate(body);
      return response.data;
    },
    {
      onSuccess: (_, { filters }) => {
        queryClient.invalidateQueries(teamMandatesKeys.list(filters));
      },
    },
  );
};

export const useUnassignMandate = (): UseMutationResult<
  void,
  APIError,
  {
    body: TrialManagementApiUnassignMandateRequest;
    filters: TrialManagementApiGetTeamMandatesRequest;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ body }) => {
      const response = await MatchingApi.trialManagement.unassignMandate(body);
      return response.data;
    },
    {
      onSuccess: (_, { filters }) => {
        queryClient.invalidateQueries(teamMandatesKeys.list(filters));
      },
    },
  );
};

export const useTrialAddEffect = (): UseMutationResult<
  void,
  APIError<TrialManagementApiAddEffectRequest>,
  TrialManagementApiAddEffectRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async (values) => {
      const res = await MatchingApi.trialManagement.addEffect(values);
      return res.data;
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(trialsKeys.detail(id));
      },
    },
  );
};

export const useTrialDeleteEffect = (): UseMutationResult<
  void,
  APIError<TrialManagementApiRemoveEffectRequest>,
  TrialManagementApiRemoveEffectRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async (values) => {
      const res = await MatchingApi.trialManagement.removeEffect(values);
      return res.data;
    },
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(trialsKeys.detail(id));
      },
    },
  );
};

export const useResetTrialCache = (): UseMutationResult<
  void,
  APIError,
  { trialId: string },
  unknown
> => {
  return useMutation(async ({ trialId }) => {
    const response = await MatchingApi.trialManagement.resetTrialCache({ id: trialId });
    return response.data;
  });
};

export const useAddCTTrial = (): UseMutationResult<
  void,
  APIError,
  { id: string; reprocessingConfirmed: boolean },
  unknown
> => {
  return useMutation(async ({ id, reprocessingConfirmed }) => {
    const response = await MatchingApi.trialManagement.addCT({
      id,
      trialAddCTBody: { reprocessingConfirmed },
    });
    return response.data;
  });
};
